import {
  json,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import invariant from "tiny-invariant";
import { capitalize } from "~/utils/stringUtils";

import { Job } from "~/types/Job";
import { companies } from "~/constants/companies";
import { JobsPage } from "~/components/JobsPage";
import { getSession } from "~/sessions";
import { fetchJobs } from "~/utils/fetchJobs";
export { headers } from "~/defaults.server";

export const meta: MetaFunction = ({ params }) => [
  {
    title: `Latest ${capitalize(
      params.companyId || ""
    )} Software Engineering Jobs in Europe`,
  },
  {
    name: "description",
    content: `Get notified when ${capitalize(
      params.companyId || ""
    )} posts a new job.`,
  },
  {
    tagName: "link",
    rel: "canonical",
    href: `https://nextleveljobs.eu/companies/${params.companyId?.toLowerCase()}`,
  },
];

export async function loader({ params, request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get("Cookie"));
  const accessToken = session.get("accessToken");
  invariant(params.companyId, "Missing params");

  const { id, name } = companies.find(
    (c) => c.id.toLowerCase() === params.companyId?.toLowerCase()
  ) || { id: "" };

  if (!id)
    return json({
      count: 0,
      status: 404,
      company: "",
      jobs: null,
      accessToken,
    });

  if (params.companyId !== params.companyId.toLowerCase()) {
    throw redirect(`/companies/${id.toLowerCase()}`);
  }

  const jobs = await fetchJobs({
    companies: [id],
    size: 15,
  });

  if (jobs.error) {
    return json({
      status: 500,
      company: "",
      jobs: null,
      accessToken,
      error: jobs.error,
      count: 0,
    });
  }

  return json({
    company: name,
    jobs: jobs.jobs,
    accessToken,
    count: jobs.count,
  });
}

export default function CompanyPage() {
  const { company, jobs, count } = useLoaderData<typeof loader>();
  if (!company) return null;

  return (
    <JobsPage
      title={
        <>
          Latest <span className="text-lime-400">{company}</span> Software
          Engineering Jobs in Europe
        </>
      }
      company={company}
      jobs={(jobs as Job[]) || []}
      count={count}
    />
  );
}
